import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { ACTIVE_NETWORK, isCorrectNetwork } from '../config/networks';

const WalletContext = createContext(null);

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within WalletProvider');
  }
  return context;
};

export const WalletProvider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true); // Track initial load
  const [isCorrectChain, setIsCorrectChain] = useState(false);
  const [error, setError] = useState(null);
  
  const isInitialized = useRef(false);

  // Get ethereum provider
  const getEthereumProvider = () => {
    if (typeof window === 'undefined') return null;
    
    // Check for various wallet providers
    if (window.ethereum) {
      return window.ethereum;
    }
    
    // Trust Wallet
    if (window.trustwallet) {
      return window.trustwallet;
    }
    
    // Check for other injected providers
    if (window.web3?.currentProvider) {
      return window.web3.currentProvider;
    }
    
    return null;
  };

  // Switch to correct network
  const switchNetwork = async () => {
    const ethereum = getEthereumProvider();
    if (!ethereum) return false;

    try {
      await ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: ACTIVE_NETWORK.chainIdHex }]
      });
      return true;
    } catch (switchError) {
      // Chain not added, try to add it
      if (switchError.code === 4902) {
        try {
          await ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: ACTIVE_NETWORK.chainIdHex,
              chainName: ACTIVE_NETWORK.name,
              nativeCurrency: ACTIVE_NETWORK.currency,
              rpcUrls: [ACTIVE_NETWORK.rpcUrl],
              blockExplorerUrls: [ACTIVE_NETWORK.blockExplorer]
            }]
          });
          return true;
        } catch (addError) {
          console.error('Error adding network:', addError);
          setError('Failed to add network. Please add it manually.');
          return false;
        }
      }
      console.error('Error switching network:', switchError);
      setError('Failed to switch network. Please switch manually.');
      return false;
    }
  };

  // Connect wallet
  const connect = async () => {
    const ethereum = getEthereumProvider();
    
    if (!ethereum) {
      setError('No wallet found. Please install MetaMask or Trust Wallet.');
      return false;
    }

    setIsConnecting(true);
    setError(null);

    try {
      // Request accounts
      const accounts = await ethereum.request({
        method: 'eth_requestAccounts'
      });

      if (accounts.length === 0) {
        throw new Error('No accounts found');
      }

      // Get chain ID
      const chainIdHex = await ethereum.request({ method: 'eth_chainId' });
      const currentChainId = parseInt(chainIdHex, 16);
      
      // Check if on correct network
      if (!isCorrectNetwork(currentChainId)) {
        const switched = await switchNetwork();
        if (!switched) {
          setIsConnecting(false);
          return false;
        }
      }

      // Create provider and signer
      const web3Provider = new ethers.providers.Web3Provider(ethereum, 'any');
      const web3Signer = web3Provider.getSigner();

      // Trust Wallet fix: Wait for signer to be ready
      // Try to get the address to ensure signer is initialized
      try {
        await web3Signer.getAddress();
      } catch (e) {
        // Wait a bit for Trust Wallet to initialize
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      setAccount(accounts[0]);
      setChainId(currentChainId);
      setProvider(web3Provider);
      setSigner(web3Signer);
      setIsCorrectChain(isCorrectNetwork(currentChainId));
      
      // Small delay only for Trust Wallet to fully initialize
      if (ethereum.isTrust || window.trustwallet) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      setIsConnecting(false);

      return true;
    } catch (err) {
      console.error('Connection error:', err);
      setError(err.message || 'Failed to connect wallet');
      setIsConnecting(false);
      return false;
    }
  };

  // Disconnect wallet
  const disconnect = () => {
    setAccount(null);
    setChainId(null);
    setProvider(null);
    setSigner(null);
    setIsCorrectChain(false);
    setError(null);
  };

  // Handle account changes
  const handleAccountsChanged = (accounts) => {
    if (accounts.length === 0) {
      disconnect();
    } else if (accounts[0] !== account) {
      setAccount(accounts[0]);
      // Update signer with new account
      const ethereum = getEthereumProvider();
      if (ethereum) {
        const web3Provider = new ethers.providers.Web3Provider(ethereum, 'any');
        setSigner(web3Provider.getSigner());
        setProvider(web3Provider);
      }
    }
  };

  // Handle chain changes
  const handleChainChanged = (chainIdHex) => {
    const newChainId = parseInt(chainIdHex, 16);
    setChainId(newChainId);
    setIsCorrectChain(isCorrectNetwork(newChainId));
    
    // Update provider without page refresh
    const ethereum = getEthereumProvider();
    if (ethereum && account) {
      const web3Provider = new ethers.providers.Web3Provider(ethereum, 'any');
      setProvider(web3Provider);
      setSigner(web3Provider.getSigner());
    }
    
    // If wrong network, attempt to switch
    if (!isCorrectNetwork(newChainId) && account) {
      switchNetwork();
    }
  };

  // Setup event listeners
  useEffect(() => {
    const ethereum = getEthereumProvider();
    if (!ethereum) return;

    ethereum.on('accountsChanged', handleAccountsChanged);
    ethereum.on('chainChanged', handleChainChanged);

    return () => {
      ethereum.removeListener('accountsChanged', handleAccountsChanged);
      ethereum.removeListener('chainChanged', handleChainChanged);
    };
  }, [account]);

  // Check for existing connection on mount
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const checkConnection = async () => {
      const ethereum = getEthereumProvider();
      if (!ethereum) {
        setIsInitializing(false);
        return;
      }

      try {
        // Detect Trust Wallet
        const isTrustWallet = ethereum.isTrust || !!window.trustwallet;
        
        let accounts = [];
        
        if (isTrustWallet) {
          // Trust Wallet requires eth_requestAccounts - eth_accounts returns empty
          try {
            accounts = await ethereum.request({ method: 'eth_requestAccounts' });
          } catch (e) {
            // User rejected or not connected
            console.log('Trust Wallet not connected or rejected');
          }
        } else {
          // Other wallets work with eth_accounts
          accounts = await ethereum.request({ method: 'eth_accounts' });
        }
        
        if (accounts.length > 0) {
          const chainIdHex = await ethereum.request({ method: 'eth_chainId' });
          const currentChainId = parseInt(chainIdHex, 16);
          
          const web3Provider = new ethers.providers.Web3Provider(ethereum, 'any');
          const web3Signer = web3Provider.getSigner();

          // Trust Wallet fix: ensure signer is ready
          try {
            await web3Signer.getAddress();
          } catch (e) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }

          setAccount(accounts[0]);
          setChainId(currentChainId);
          setProvider(web3Provider);
          setSigner(web3Signer);
          setIsCorrectChain(isCorrectNetwork(currentChainId));

          // Auto-switch if wrong network
          if (!isCorrectNetwork(currentChainId)) {
            switchNetwork();
          }
        }
      } catch (err) {
        console.error('Error checking connection:', err);
      }
      
      // Mark initialization complete
      setIsInitializing(false);
    };

    // Only Trust Wallet (or a provider that isn't injected yet) needs the warm-up delay
    const eth = getEthereumProvider();
    const needsDelay = !eth || eth.isTrust || !!window.trustwallet;
    if (needsDelay) setTimeout(checkConnection, 300);
    else checkConnection();
  }, []);

  const value = {
    account,
    chainId,
    provider,
    signer,
    isConnecting,
    isInitializing,
    isConnected: !!account,
    isCorrectChain,
    error,
    connect,
    disconnect,
    switchNetwork,
    getEthereumProvider
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

export default WalletContext;